import { appendMarketingParams } from './marketingParams'

const BOOKING_WIDGET_TARGET_KEY = 'booking-widget-target'
const DEFAULT_BOOKING_WIDGET_TARGET = 'book-ship'

/** Canonical booking mount; Hydra maps it to each brand's configured flow. */
const BOOK_AGAIN_PATH = '/book/ship'

/** Query-param source — `URLSearchParams` or Next's `ReadonlyURLSearchParams`. */
type ParamSource = { get(name: string): string | null } | null | undefined

/** Only the address fields forwarded to the booking flow. */
interface BookingWidgetAddress {
  address_1?: string
  city?: string
  state?: string
  zip?: string
  country_code?: string
  /** Business name for establishment picks (set by useAutoCompleteAddress). */
  company_name?: string
}

interface BookingWidgetPlace {
  id?: string
}

export interface BuildBookingWidgetQueryArgs {
  originAddress?: BookingWidgetAddress | null
  destinationAddress?: BookingWidgetAddress | null
  originRecognizedPlace?: BookingWidgetPlace | null
  destinationRecognizedPlace?: BookingWidgetPlace | null
  tripType?: string
  searchParams?: ParamSource
}

/**
 * Appends a single ship point (origin or destination) to `params`. Prefers a
 * recognized place id; otherwise falls back to the raw address fields.
 *
 * @param prefix      `'o'` for origin, `'d'` for destination
 * @param placeParam  the recognized-place param name (`shipping_from`/`shipping_to`)
 */
const appendShipPoint = (
  params: URLSearchParams,
  prefix: 'o' | 'd',
  placeParam: 'shipping_from' | 'shipping_to',
  place?: BookingWidgetPlace | null,
  address?: BookingWidgetAddress | null,
): void => {
  if (place?.id) {
    params.set(placeParam, place.id)
    return
  }

  if (!address) return

  if (address.address_1) params.set(`${prefix}_address`, address.address_1)
  if (address.city) params.set(`${prefix}_city`, address.city)
  if (address.state) params.set(`${prefix}_state`, address.state)
  if (address.zip) params.set(`${prefix}_zip`, address.zip)
  if (address.country_code) params.set(`${prefix}_country`, address.country_code)
  if (address.company_name) params.set(`${prefix}_company`, address.company_name)
}

/**
 * Builds the query string the booking widget forwards to `/book/ship` (or
 * `/tac-book`). Carries the address/route, trip type, coupon, and — critically —
 * the marketing/attribution params (utm_*, fbclid, click_id, …) from the
 * incoming URL so they are never dropped on the hop out of the widget.
 *
 * Pure and side-effect free so it can be unit-tested directly.
 *
 * @returns URLSearchParams string (without the leading `?`)
 */
export const buildBookingWidgetQuery = ({
  originAddress,
  destinationAddress,
  originRecognizedPlace,
  destinationRecognizedPlace,
  tripType,
  searchParams,
}: BuildBookingWidgetQueryArgs): string => {
  // Build query string params from address data (same pattern as Rails BookingWidget)
  const params = new URLSearchParams()

  appendShipPoint(params, 'o', 'shipping_from', originRecognizedPlace, originAddress)
  appendShipPoint(params, 'd', 'shipping_to', destinationRecognizedPlace, destinationAddress)

  if (tripType) params.set('trip_type', tripType)

  const coupon = searchParams?.get('coupon')
  if (coupon) params.set('coupon', coupon)

  // Preserve marketing/attribution params (utm_*, fbclid, click_id, …) so they
  // survive the redirect to /book/ship (and the full-page /tac-book navigation).
  appendMarketingParams(params, searchParams)

  return params.toString()
}

/**
 * Navigates from the booking widget to the configured target route.
 *
 * Resolution order (first match wins):
 *  1. `localStorage['booking-widget-target']` — per-session override for
 *     manual QA / cutover testing without redeploying.
 *  2. `NEXT_PUBLIC_BOOKING_WIDGET_TARGET` — build/deploy-time env var so a
 *     brand env (Hydra `brand_env`) can flip the whole site between targets.
 *  3. `'book-ship'` — hard-coded fallback that keeps the widget pointed at
 *     the Kettle-native flow when nothing is configured.
 *
 * Recognized values:
 *  - `'tac-book'` → `/tac-book` (cruises-next / TAC, mounted by Hydra)
 *  - anything else → `/book/ship` (the Kettle booking flow)
 *
 * Both targets use a full-page navigation (`window.location.assign`) rather than
 * a client-side `router.push`. `/book/ship` is a statically-exported route
 * (`output: 'export'`) whose RSC payload ships as a sibling `.txt` file served
 * as `text/plain`; an App Router *soft* navigation into it can load that raw
 * payload as a top-level document, so the user sees the Flight payload as
 * unstyled text instead of the booking page (GSE-11866). A hard navigation
 * requests the prerendered HTML directly and never touches the `.txt`.
 *
 * Nothing is lost across the reload: the query params ride in the URL, and the
 * cart/coupon stores are persisted (localStorage/sessionStorage) and rehydrated
 * on the booking flow's mount.
 *
 * @param queryString  Pre-built URLSearchParams string (without the leading `?`)
 */
export const redirectFromBookingWidget = (queryString: string): void => {
  const stored =
    typeof window !== 'undefined' ? localStorage.getItem(BOOKING_WIDGET_TARGET_KEY) : null
  const envDefault = process.env.NEXT_PUBLIC_BOOKING_WIDGET_TARGET || DEFAULT_BOOKING_WIDGET_TARGET
  const target = stored ?? envDefault
  const path = target === 'tac-book' ? '/tac-book' : '/book/ship'

  hardNavigate(path, queryString)
}

/**
 * Navigates from an account "Book again" action to the brand's re-booking flow
 * (GSE-11919).
 *
 * Always targets `/book/ship` — the canonical booking mount — and lets the
 * environment decide which app answers it. Hydra already knows which flow each
 * brand gets (`mount_allows_brand`), so `/book/ship` serves TAC (cruises-next)
 * on ShipSticks and Ship&Play and Kettle on Ship Skis. That *is* the per-brand
 * routing this ticket asks for, resolved from config rather than restated here.
 *
 * It deliberately does NOT branch on `brand.key` to a literal `/tac-book`. Those
 * paths have no fixed meaning across the fleet: `/tac-book` 301s to `/book` on
 * master.staging and 26-12-03-integration but 404s on dev2, which dead-ended
 * ShipSticks Book Again there. Mount paths move (cruises-next was remounted from
 * `/tac-book` to `/book` on 2026-09-03) and each brand's configured flow moves
 * with them — Ship Skis is itself migrating to TAC — so a hard-coded path is
 * wrong again after the next mount change.
 *
 * Also does NOT consult the `booking-widget-target` flag: Book Again follows the
 * brand's configured flow, not the widget's cutover override.
 *
 * @param queryString  Pre-built URLSearchParams string (without the leading `?`)
 */
export const redirectBookAgain = (queryString: string): void => {
  hardNavigate(BOOK_AGAIN_PATH, queryString)
}

/**
 * Full-page navigation via `window.top` so it escapes an iframe embed (the
 * booking widget is served inside an iframe on legacy Rails pages). Hard —
 * never `router.push` — because `/book/ship` is statically exported and a soft
 * navigation can render its raw RSC payload (GSE-11866).
 */
const hardNavigate = (path: string, queryString: string): void => {
  const url = queryString ? `${path}?${queryString}` : path

  const nav = window.top ?? window
  nav.location.assign(url)
}
